/**
 * 商品の栄養値の「疑い」を見る規則 — 純粋な関数としきい値（2026-10-31〜・入口 `@kalori/nutrition/quality`）。
 *
 * 面（ランキング・おすすめ・監査）は `@kalori/catalog/data-quality` から再輸出されたものを使い、夜間の取り込み
 * （scraper の merge・健全性・再分析の採否）と運営・事業者の画面はここを直に使う。しきい値を自前で持たない
 * （2026-10-31 までは熱量の検算が 10 か所以上にあり、しきい値が 0.15 / 0.2 / 0.25 / 0.3、比の分母が 2 通り、酒の外し方が
 * 純アルコール量を足す・分類で見る・名前の正規表現 3 通りと面ごとに違った）。
 *
 * - 熱量の検算: 表示の kcal と 4P+9F+4C(+7×アルコール) の食い違い（{@link energyGap}）と、目的ごとの水準
 * - 酒の扱い: アルコールは 7kcal/g で PFC に出ない。1 つの規則（{@link energyGap} の `alcoholic`）
 * - 1 つのマクロだけで総 kcal を超える（{@link hasImplausibleMacro}）
 * - 1 人前の kcal の上限（収集で落とす・ランキングで外す・人が見る）
 *
 * AI・通信・DB に依存しない（docs/architecture/shared-packages.md）。SQL の式（{@link atwaterKcalSql}）は文字列を返すだけ。
 */
// ── 熱量の検算 ───────────────────────────────────────────────────────────
/** Atwater の一般係数（kcal/g）。アルコールは PFC に出ない 7kcal/g。 */
const KCAL_PER_GRAM = { protein: 4, fat: 9, carbs: 4, alcohol: 7 };
/**
 * 4P + 9F + 4C（+ 7×アルコール）。**欠けた項目は 0 として足す**（「PFC から計算すると ◯ kcal」の表示・
 * 熱量が無いのに PFC だけ大きい行の検出に使う）。食い違いの判定には {@link energyGap} を使う（欠けたら判定しない）。
 */
export function atwaterKcal(n) {
    return (KCAL_PER_GRAM.protein * (n.protein ?? 0) +
        KCAL_PER_GRAM.fat * (n.fat ?? 0) +
        KCAL_PER_GRAM.carbs * (n.carbs ?? 0) +
        KCAL_PER_GRAM.alcohol * (n.alcohol ?? 0));
}
/**
 * {@link atwaterKcal} の SQL 版（D1 / SQLite・DuckDB 共通の算術）。`alias` は表の別名（`p` → `p.protein`）。
 * NULL の項目があれば式は NULL（SQL の算術のまま）＝ 判定しない側に倒れる。アルコールの列は持たない。
 */
export function atwaterKcalSql(alias) {
    return `(${KCAL_PER_GRAM.protein} * ${alias}.protein + ${KCAL_PER_GRAM.fat} * ${alias}.fat + ${KCAL_PER_GRAM.carbs} * ${alias}.carbs)`;
}
/**
 * 表示の kcal と 4P+9F+4C の食い違い。判定できないときは null:
 * - kcal が無い・0 以下（0kcal の品は比が定まらない）
 * - P・F・C のどれかが無い（欠けた項目を 0 として足すと、脂質未入力の品が「9kcal/g 低い正しい値」に見えて
 *   本物の誤りと区別できない）
 * - **酒の規則**: `alcoholic`（酒と分かっている品）で純アルコール量（`alcohol`）が分からない。アルコールの
 *   7kcal/g は PFC に出ないので、kcal と PFC がずれるのが正しい（ビール 140kcal・P1 F0 C10 = 44kcal）。
 *   純アルコール量が分かれば 7kcal/g を足して普通に検算する（asahi-beer の公式の表）
 */
export function energyGap(n, opts) {
    const { heat, protein, fat, carbs, alcohol } = n;
    if (heat == null || !(heat > 0) || protein == null || fat == null || carbs == null)
        return null;
    if (alcohol == null && opts?.alcoholic)
        return null;
    const computed = atwaterKcal({ protein, fat, carbs, alcohol });
    const diffKcal = computed - heat;
    return { declared: heat, computed, diffKcal, ratio: Math.abs(diffKcal) / heat };
}
const ROUNDING_KCAL = 2;
/**
 * 「値が 4/9/4 と噛み合わない＝誤り」として**手を打つ**水準（30%・2kcal）。
 * - merge（`@kalori/nutrition/merge`）: 公式の kcal と公式の PFC がこれを超えたら公式の PFC を誤ラベルとみなし、
 *   全項目を AI 推定に替える。AI で埋めた PFC を公式の kcal に揃える（alignEstimatesToOfficialEnergy）のもこの水準
 * - 事業者の申請・承認画面・確認のおすすめ（merchant-api・admin）: 夜間の merge と同じ判定を見せる
 * - scraper の再推定の関門（agent-analysis）・再分析の採否（reanalysis-review）・低評価の再分析の候補（feedback-heal）
 * - 監査（tools の nutrition-audit の official-energy）と品質レポート（tools の reports/data-quality）
 * 公式の値を捨てる・推定を書かない判断に使うので、収集時の検査（{@link ENERGY_SCRAPE_CHECK}）より緩い。
 * 食物繊維（2kcal/g）・糖アルコールで正しい品も 2 割ほどずれる（2026-08-05 の全店実測で、正常な店でも 2 割を超える
 * 品が最大 17% あった — lotte の糖アルコール菓子）
 */
export const ENERGY_MISMATCH = { ratio: 0.3, minKcal: ROUNDING_KCAL };
/**
 * 収集の時点で、公式の表の**行を読み違えていないか**を見る水準（20%・2kcal）。
 * - scraper の店単位の健全性（health/nutrition.ts）: この水準を超える品が 4 割を超えたら列ずれ（店ごとの率で見るので
 *   単品の誤検知は数に埋もれる。2026-08-05 の全店実測で正常店の最悪は 17%・mcdonalds の列ずれはほぼ全品が超えた）
 * - 店のパーサ（taco-bell・frijoles）: 超えた行の PFC を捨てて推定に回す・fujiya-restaurant: 推定 PFC を公式の kcal へ縮める
 * 正しく読めた公式の行はほぼ一致するので {@link ENERGY_MISMATCH} より厳しくしてよい（外れても PFC が推定に回るだけ）。
 */
export const ENERGY_SCRAPE_CHECK = { ratio: 0.2, minKcal: ROUNDING_KCAL };
/**
 * 推定の自己整合の**注意**（15%・2kcal）。書く・出すのは止めず、人が読む警告・目視の手がかりにだけ使う。
 * - 一般メニューの推定の検証（scraper の generic-menu-analyzer）
 * - 抜き取り監査の目視の手がかり（tools の nutrition-audit の hints）
 */
export const ENERGY_WARN = { ratio: 0.15, minKcal: ROUNDING_KCAL };
/** 食い違いが水準を超えたか（判定できない null は超えない）。 */
export function exceedsEnergyGap(gap, level) {
    return gap != null && gap.ratio > level.ratio && Math.abs(gap.diffKcal) > level.minKcal;
}
/**
 * kcal と P・F・C が {@link ENERGY_MISMATCH} を超えて噛み合わないか（夜間の merge が公式の誤ラベルとみなす判定）。
 * `alcoholic` は酒の規則（{@link energyGap}）— 酒と分かっている品は判定しない。
 */
export function hasNutritionInconsistency(heat, protein, fat, carbs, opts) {
    return exceedsEnergyGap(energyGap({ heat, protein, fat, carbs }, opts), ENERGY_MISMATCH);
}
// ── 酒 ──────────────────────────────────────────────────────────────────
/**
 * 酒の名前（正規表現の本体。JS と DuckDB の RE2 の両方で動くよう先読みを使わない）。
 * ⚠️ 食品と語を共有する短い語は入れない — 「ジン」はニンジン・ジンジャー、「ラム」はラム肉・ラムレーズン、
 * 「酒」は酒蒸し・酒粕に当たる（2026-10-31 まで監査が入れていた）。名前で拾えない酒（「ジンライム」）は
 * AI の分類（food_category の alcohol）で拾う。「サワー」「ワイン」はサワークリーム・ワイン煮にも当たるが、
 * 外れても検算しないだけ（誤りを見逃す側）なので残す。
 */
export const ALCOHOLIC_DRINK_NAME_PATTERN = "ビール|発泡酒|チューハイ|酎ハイ|焼酎|ハイボール|サワー|日本酒|清酒|梅酒|果実酒|紹興酒|ワイン|ウイスキー|ウィスキー|ブランデー|リキュール|カクテル|マッコリ|テキーラ|ウォッカ|マルガリータ|サングリア";
const ALCOHOLIC_DRINK_NAME_RE = new RegExp(ALCOHOLIC_DRINK_NAME_PATTERN);
/**
 * 酒か（熱量の検算の酒の規則に渡す `alcoholic`）。**AI の分類（product_analyses.food_category）があればそれで決め**
 * （`alcohol` を含むか）、分類が無い・空のとき（収集の時点・未分析）だけ名前を {@link ALCOHOLIC_DRINK_NAME_PATTERN} で見る。
 * 分類を先にするのは、名前は料理にも当たるから（2026-10-30 のマートの公開中の品: 名前が当たり分類が酒でない 219 品は
 * 「牛肉のビール煮」「あさりのワイン蒸し」「カクテルシュリンプ」「ノンアルコールビール」が大半。逆に名前で拾えない酒
 * 「国士無双」「一番搾り」は分類で 1,509 品拾える）。
 */
export function isAlcoholicDrink(item) {
    if (item.foodCategories?.length)
        return item.foodCategories.includes("alcohol");
    return item.name != null && ALCOHOLIC_DRINK_NAME_RE.test(item.name);
}
// ── 1 つのマクロだけで総 kcal を超える ───────────────────────────────────
/**
 * 1 つのマクロ（P・F・C のどれか）の kcal が総 kcal のこの倍を超えたら、物理的にあり得ない
 * （1 食分でない包装の栄養・「250g」をたんぱく質 250g と読んだ類の解析ミス）。丸め・食物繊維・糖アルコールの分の
 * 許しとして 1.3 倍まで許す。ランキングの「1 人前でない」の判定（catalog の exclusions）が使う。
 */
export const MACRO_IMPLAUSIBILITY_FACTOR = 1.3;
/** 1 つのマクロの kcal が総 kcal × {@link MACRO_IMPLAUSIBILITY_FACTOR} を超えるか。kcal が無い・0 以下は判定しない（false）。 */
export function hasImplausibleMacro(n) {
    const heat = n.heat;
    if (heat == null || heat <= 0)
        return false;
    const limit = heat * MACRO_IMPLAUSIBILITY_FACTOR;
    return (KCAL_PER_GRAM.protein * (n.protein ?? 0) > limit ||
        KCAL_PER_GRAM.carbs * (n.carbs ?? 0) > limit ||
        KCAL_PER_GRAM.fat * (n.fat ?? 0) > limit);
}
// ── 1 人前の kcal の上限 ─────────────────────────────────────────────────
//
// 4 つの水準は目的が違う（小さい順）。どれも「1 人前でない（包装まるごと・多人数・読み違い）」の疑いだが、
// 外れたときの扱いの重さで幅を変えている:
//   1,500  人が見る（監査の「方針待ち」の数）   … 正しい大皿も混ざるので数えるだけ
//   2,500  収集で落とす（公式の値）              … 落とした品はカタログに入らない。正しい 1 人前の最大級（大盛りの定食・
//                                                   ピザ 1 枚）より上に置く
//   3,000  ランキングから外す（表示中の値）       … 推定は収集の上限を通らないので、ランキングの最後の防波堤
//   5,000  推定を書かない（エージェントの再推定） … 値域の検査。これを超えるのは量の取り違え
/**
 * AI 推定の kcal がこれ以上なら、包装・大皿まるごとの推定かもしれないとして**人が見る**
 * （tools の nutrition-audit の型の監査 `whole-package-ai`・方針を決めるまでの数）。
 */
export const LARGE_ESTIMATE_REVIEW_KCAL = 1500;
/**
 * 収集した**公式の** kcal がこれを超える行は、1 人前として異常として**落とす**（scraper の product-filter の既定。
 * 店ごとに `maxCalories` で下げられる — chateraise 1,500）。抜き取り監査はこれを超える表示に目視の手がかりを付ける。
 */
export const SCRAPE_MAX_SERVING_KCAL = 2500;
/**
 * 表示中の kcal がこれを超える品は、名前の目印が無くても多人数・まとめ買いとみなし**ランキングから外す**
 * （catalog の exclusions の `isOversizedForRanking`）。推定の値は収集の上限（{@link SCRAPE_MAX_SERVING_KCAL}）を
 * 通らないので、ここが最後の防波堤。品質レポートの「cal 異常」も同じ上限で数える。
 */
export const RANKING_MAX_SERVING_KCAL = 3000;
/**
 * エージェントの再推定（scraper の agent-analysis）で、kcal がこれを超える推定は量の取り違え（100g 当たり・箱ごと）
 * として**書かない**。値域の検査なので他より広い（書いた後はランキングの上限が別に効く）。
 */
export const ESTIMATE_MAX_SERVING_KCAL = 5000;
//# sourceMappingURL=quality.js.map